import { useCallback, useEffect, useState } from "react";
import { Loader2, Power } from "lucide-react";
import DashboardLayout from "../../layouts/DashboardLayout";
import api from "../../api";
import { KIND_LABELS, PLATFORM_STATUS_LABELS, STATUS_TONES, formatDate } from "../../components/help/requestLabels";

// NL Labs: requests clinics forwarded from Help (backend help/feature_requests.py).
// Setting an outcome tells the person who asked and their clinic's admins.
const FILTERS = ["new", "planned", "switched_on", "done", "declined", "all"];
const OUTCOMES = ["new", "planned", "done", "declined"];

function Row({ r, onSaved }) {
  const [status, setStatus] = useState(r.platform_status || "new");
  const [note, setNote] = useState(r.platform_note || "");
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const save = async (switchOn = false) => {
    setBusy(switchOn ? "switch" : "save");
    setError(null);
    try {
      const res = await api.updateForwardedRequest(r.id, { platform_status: status, note, switch_on: switchOn });
      onSaved({ ...r, ...res.data.request });
    } catch (e) {
      setError(e?.response?.data?.error || "Couldn't save. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-xl border border-secondary-200 bg-white p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-secondary-900 font-medium">{r.question}</p>
          {r.details && <p className="text-sm text-secondary-700 mt-1 whitespace-pre-line">{r.details}</p>}
          <p className="text-xs text-secondary-500 mt-1">
            {r.org_name} · {r.user_label || "Staff"}{r.role ? ` (${r.role})` : ""} · forwarded {formatDate(r.forwarded_at)} ·{" "}
            {KIND_LABELS[r.kind] || r.kind}{r.feature_label ? `: ${r.feature_label}` : ""}
          </p>
          {r.admin_note && <p className="text-sm text-secondary-700 bg-secondary-50 rounded-lg px-3 py-2 mt-2"><span className="font-medium">Clinic admin:</span> {r.admin_note}</p>}
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_TONES[r.platform_status] || STATUS_TONES.new}`}>
          {PLATFORM_STATUS_LABELS[r.platform_status] || "Received"}
        </span>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          className="rounded-lg border border-secondary-300 px-3 py-2 text-sm text-secondary-900">
          {OUTCOMES.map((o) => <option key={o} value={o}>{PLATFORM_STATUS_LABELS[o]}</option>)}
          {r.platform_status === "switched_on" && <option value="switched_on">{PLATFORM_STATUS_LABELS.switched_on}</option>}
        </select>
        <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000}
          placeholder="Reply to the clinic (they see this)"
          className="flex-1 min-w-0 rounded-lg border border-secondary-300 px-3 py-2 text-sm text-secondary-900" />
        <button type="button" onClick={() => save(false)} disabled={!!busy}
          className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60">
          {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Save"}
        </button>
      </div>
      {r.kind === "not_on" && r.feature && r.platform_status !== "switched_on" && (
        <button type="button" onClick={() => save(true)} disabled={!!busy}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-800 text-sm font-medium hover:bg-emerald-100 disabled:opacity-60">
          {busy === "switch" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
          Switch on {r.feature_label || r.feature} for {r.org_name}
        </button>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}

export default function AdminFeatureRequests() {
  const [filter, setFilter] = useState("new");
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setRows(null);
    api.fetchForwardedRequests(filter === "all" ? null : filter)
      .then((r) => setRows(r.data.requests || []))
      .catch((e) => setError(e?.response?.data?.error || "Couldn't load requests."));
  }, [filter]);
  useEffect(load, [load]);

  return (
    <DashboardLayout>
      <div className="container mx-auto p-4 md:p-8 max-w-5xl space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Feature Requests</h1>
          <p className="text-sm text-gray-500 mt-1">
            What clinics asked Help for that MindConnect couldn't do, forwarded by their admin. Your reply and outcome
            go to the person who asked and their clinic's admins.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button key={f} type="button" onClick={() => setFilter(f)}
              className={`text-sm px-3 py-1.5 rounded-full border ${filter === f ? "bg-primary-600 text-white border-primary-600" : "bg-white text-secondary-700 border-secondary-300 hover:border-primary-400"}`}>
              {f === "all" ? "All" : PLATFORM_STATUS_LABELS[f]}
            </button>
          ))}
        </div>
        {error && <p className="text-red-600">{error}</p>}
        {!rows && !error && <p className="text-secondary-500">Loading…</p>}
        {rows && rows.length === 0 && <p className="text-secondary-500">No requests here.</p>}
        <div className="space-y-3">
          {(rows || []).map((r) => (
            <Row key={r.id} r={r} onSaved={(u) => setRows((all) => all.map((x) => (x.id === u.id ? u : x)))} />
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
